import React, { useContext, memo } from 'react';
import styled, { keyframes } from 'styled-components';
import Image from './Image';

import { AnalyticsContext } from './AnalyticsProvider';
import { PageContext } from './PageProvider';
import Markdown from './Markdown';

import BREAKPOINTS from '../styles/breakpoints';
import COLORS from '../styles/colors';
import TYPO from '../styles/typography';

import thumbsUp from '../assets/icons/thumbs-up.png';

const emojiAnimation = keyframes`
  0% { transform: rotate(0); }
  10% { transform: rotate(-10deg); }
  20% { transform: rotate(12deg); }
  30% { transform: rotate(-10deg); }
  40% { transform: rotate(9deg); }
  50% { transform: rotate(0); }
  100% { transform: rotate(0); }
`;

const Container = styled.div`
  position: fixed;
  width: 278px;
  min-height: 150px;
  left: 20px;
  bottom: 20px;
  pointer-events: none;
  z-index: 10;

  ${BREAKPOINTS.min.medium`
    left: 40px;
    bottom: 40px;
  `}
`;

const Bar = styled.div`
  ${TYPO.p1}
  padding: 20px;
  background-color: ${COLORS.shades.s100.css};
  color: ${COLORS.white.css};
  border-radius: 10px;

  transform-origin: 50% 50%;
  transition: transform 0.2s ease, opacity 0.2s ease;

  transform: scale(1);
  opacity: 1;
  pointer-events: all;

  &[aria-hidden='true'] {
    transform: scale(0.8);
    opacity: 0;
    pointer-events: none;

    & a[href],
    & area[href],
    & input:not([disabled]),
    & select:not([disabled]),
    & textarea:not([disabled]),
    & button:not([disabled]),
    & [tabindex]:not([disabled]),
    & [contenteditable='true']:not([disabled]) {
      visibility: hidden;
    }
  }

  box-shadow: 0 1.4px 2.2px rgba(0, 0, 0, 0.011),
    0 3.5px 5.3px rgba(0, 0, 0, 0.016), 0 6.5px 10px rgba(0, 0, 0, 0.02),
    0 11.6px 17.9px rgba(0, 0, 0, 0.024), 0 21.7px 33.4px rgba(0, 0, 0, 0.029),
    0 52px 80px rgba(0, 0, 0, 0.04);
`;

const DisclaimerText = styled(Markdown)`
  p {
    ${TYPO.p2}
  }
`;

const Buttons = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: space-between;
`;

type ButtonProps = {
  $brand?: boolean;
};
const Button = styled.button<ButtonProps>`
  display: block;
  font: inherit;
  ${TYPO.p2}
  font-weight: 500;
  color: ${(props) =>
    props.$brand ? COLORS.brand.regular.css : COLORS.shades.s400.css};
`;

const ImageContainer = styled.div`
  margin-left: 5px;
  display: inline-block;
  animation: ${emojiAnimation} 2.5s ease infinite;
  transform-origin: 0 70%;
`;

const CookieBanner = () => {
  const { generic } = useContext(PageContext);
  const analytics = useContext(AnalyticsContext);

  return (
    <Container suppressHydrationWarning>
      <Bar aria-hidden={!analytics.askConsent}>
        <DisclaimerText content={generic.cookieDisclaimer} />
        <Buttons>
          <Button
            onClick={() => {
              analytics.acceptsCookies('0');
            }}
            type="button"
          >
            Reject
          </Button>
          <Button
            onClick={() => {
              analytics.acceptsCookies('1');
            }}
            type="button"
            $brand
          >
            Accept{' '}
            <ImageContainer>
              <Image
                src={thumbsUp.src}
                width={16}
                height={16}
                alt="thumbsup emoji"
              />
            </ImageContainer>
          </Button>
        </Buttons>
      </Bar>
    </Container>
  );
};

export default memo(CookieBanner);
